/**
 * The consent URL a signed-out user was on, kept across the login round trip.
 *
 * The general deep link (`pendingDeepLink` in AuthProvider) lives in memory, so
 * a reload on the login page — or a TOTP typo that makes someone start over in
 * a new tab — loses it. For most of the console that costs a click. For OAuth
 * consent it strands the user: the AI app is waiting on a request id nobody can
 * type back in. So this one path is kept in sessionStorage (tab-scoped, gone
 * when the tab closes) and is consumed exactly once after sign-in (plan D-96).
 *
 * Only a path starting `/oauth/consent` is ever stored or returned, so the key
 * cannot be turned into an open redirect by anything else writing to it.
 */
export const PENDING_OAUTH_KEY = 'svet.pendingOAuth';

const CONSENT_PATH = /^\/oauth\/consent(?:[?#]|$)/;

function store(): Storage | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
  } catch {
    return null;
  }
}

export function isConsentPath(path: string): boolean {
  return CONSENT_PATH.test(path);
}

export function savePendingConsent(path: string): void {
  if (!isConsentPath(path)) return;
  try {
    store()?.setItem(PENDING_OAUTH_KEY, path);
  } catch {
    /* storage full or blocked: the in-memory deep link still covers this tab */
  }
}

/** Reads and clears the stored path; `null` unless it is a consent path. */
export function takePendingConsent(): string | null {
  const s = store();
  if (!s) return null;
  try {
    const value = s.getItem(PENDING_OAUTH_KEY);
    s.removeItem(PENDING_OAUTH_KEY);
    return value && isConsentPath(value) ? value : null;
  } catch {
    return null;
  }
}
